<template>
  <div class="exam-question-info-card w-100">
    <!-- TOP ROW  -->
    <div class="top-row">
      <div class="image-card avatar" :class="setAvatarBg">
        <img
          v-lazy="getCreatorImage"
          :alt="getCreatorName"
          class="avatar-img"
          v-if="getCreatorImage"
        />

        <div class="text color_white" :class="setAvatarBg">
          {{ getInitial }}
        </div>
      </div>

      <div class="wrapper">
        <div class="title-text">{{ getCreatorName }}</div>
        <div class="meta">{{ getSubject }}</div>
      </div>
    </div>

    <!-- MID ROW  -->
    <div class="mid-row w-100">
      <div class="column w-50">
        <div class="value">{{ getQuestionCount }}</div>
        <div class="title">QUESTIONS</div>
      </div>

      <div class="column w-50">
        <div class="value">{{ getScore }}</div>
        <div class="title">MAX. SCORE</div>
      </div>
    </div>

    <!-- BOTTOM ROW  -->
    <div class="bottom-row">
      <div class="info-row">
        <div class="title">Date Created</div>
        <div class="value">{{ getCreatedDate ? getCreatedDate : "-" }}</div>
      </div>

      <div class="info-row">
        <div class="title">Subject</div>
        <div class="value">
          {{ getSubject ? getSubject : "-" }}
        </div>
      </div>

      <div class="info-row">
        <div class="title">Class</div>
        <div class="value">
          {{ getClassName ? getClassName : "-" }}
        </div>
      </div>

      <div class="info-row">
        <div class="title">Status</div>
        <div class="value brand_accent">PENDING</div>
      </div>
    </div>

    <!-- BUTTON ROW  -->
    <div class="btn-row">
      <!-- REJECT BTN -->
      <div class="exam-btn reject-btn" @click="toggleRejectModal">
        <div class="icon icon-thumbs-down"></div>
        <div class="text">Reject</div>
      </div>

      <!-- APPROVE BTN -->
      <div class="exam-btn approve-btn" @click="toggleApproveModal">
        <div class="icon icon-thumbs-up"></div>
        <div class="text">Approve</div>
      </div>
    </div>

    <!-- MODALS -->
    <portal to="app-modals">
      <transition name="fade" v-if="show_approve_modal">
        <exam-approve-modal
          :exam_info="getDetails"
          @closeTriggered="toggleApproveModal"
        />
      </transition>

      <transition name="fade" v-if="show_publish_modal">
        <exam-published-modal @closeTriggered="togglePublishModal" />
      </transition>

      <transition name="fade" v-if="show_reject_modal">
        <exam-reject-modal
          :exam_info="getDetails"
          @closeTriggered="toggleRejectModal"
        />
      </transition>
    </portal>
  </div>
</template>

<script>
import {
  setInitial,
  getShortReadableDate,
  getDayOfWeek,
} from "@/scripts/utilities";
import { setProfileBg } from "@/scripts/profile-colors";
import ExamApproveModal from "@/components/modalComps/schoolModals/ExamApproveModal";
import ExamRejectModal from "@/components/modalComps/schoolModals/ExamRejectModal";
import ExamPublishedModal from "@/components/modalComps/schoolModals/ExamPublishedModal";

export default {
  name: "ExamQuestionInfoCard",

  components: {
    ExamApproveModal,
    ExamRejectModal,
    ExamPublishedModal,
  },

  props: {
    assessment_data: {
      type: Object,
      default: () => {},
    },

    alternative_details: {
      type: Object,
      default: () => {},
    },
  },

  computed: {
    getDetails() {
      return this.alternative_details;
    },

    getCreatorName() {
      return this.getDetails?.creator?.name || "Creator name";
    },

    getCreatorImage() {
      return this.getDetails?.creator?.image || "";
    },

    getInitial() {
      return setInitial(this.getCreatorName);
    },

    setAvatarBg() {
      return setProfileBg(this.getCreatorName);
    },

    getQuestionCount() {
      return this.getDetails?.question_count || 0;
    },

    getScore() {
      return this.getDetails?.score || 0;
    },

    getSubject() {
      return this.getDetails?.subject || "Subject name";
    },

    getClassName() {
      return this.getDetails?.class_name || "-";
    },

    getCreatedDate() {
      if (this.getDetails?.created_at) {
        let week_date = getDayOfWeek(this.getDetails?.created_at);

        let custom_date = getShortReadableDate(
          this.getDetails?.created_at?.split("T")[0]
        );

        return `${week_date} ${custom_date}`;
      } else return false;
    },
  },

  data: () => ({
    show_approve_modal: false,
    show_reject_modal: false,
    show_publish_modal: false,
  }),

  methods: {
    toggleApproveModal() {
      this.show_approve_modal = !this.show_approve_modal;
    },

    toggleRejectModal() {
      this.show_reject_modal = !this.show_reject_modal;
    },

    togglePublishModal() {
      this.show_publish_modal = !this.show_publish_modal;
    },
  },
};
</script>

<style lang="scss" scoped>
.exam-question-info-card {
  background: #ffffff;
  border: 1px solid #e5e5e5;
  box-sizing: border-box;
  box-shadow: 0px 1px 4px rgba(0, 0, 0, 0.15);
  border-radius: 15px;
  padding: 20px;

  .top-row {
    @include flex_row_start_nowrap;
    padding-bottom: 15px;
    border-bottom: 1px solid #e5e5e5;
    margin-bottom: 18px;

    .image-card {
      @include equal_min_edge(50);
      margin-right: 15px;
      border-radius: 12px;
      position: relative;

      .text {
        @include transform_center;
        color: $color_white;
        font-weight: 600;
        font-size: 15.5px;
      }
    }

    .title-text {
      font-size: 15px;
      line-height: 22px;
      margin-bottom: 2px;
      font-weight: 700;
      color: $color_text;
    }

    .meta {
      font-size: 13px;
      line-height: 18px;
      color: $color_grey_dark;
    }
  }

  .mid-row {
    @include flex_row_between_nowrap;

    .column {
      border-right: 1px solid #e5e5e5;

      &:last-of-type {
        border: none;
      }

      .value {
        font-weight: 700;
        margin-bottom: 4px;
        color: $brand_navy;
        text-align: center;
        font-size: 16px;
      }

      .title {
        font-size: 12px;
        text-align: center;
        color: $color_grey_dark;
      }
    }
  }

  .bottom-row {
    margin-top: 18px;
    margin-bottom: 16px;
    border-top: 1px solid #e5e5e5;
    border-bottom: 1px solid #e5e5e5;
    padding: 20px 0;

    .info-row {
      @include flex_row_between_nowrap;
      margin-bottom: 15px;
      font-size: 13.45px;
      color: $color_grey_dark;

      &:last-of-type {
        margin-bottom: 0;
      }

      .title {
      }

      .value {
        font-weight: 700;
      }
    }
  }

  .btn-row {
    @include flex_row_center_nowrap;

    .exam-btn {
      @include flex_row_center_nowrap;
      padding: 10px 22px;
      border-radius: 20px;
      font-size: 13.25px;
      width: max-content;
      cursor: pointer;
      color: $brand_navy;

      .icon {
        margin-right: 12px;
        font-size: 14.5px;
      }

      .text {
        // transition: all 0.4s ease-in-out;
      }

      &:hover {
        box-shadow: 0px 2px 5px rgba(0, 0, 0, 0.1);
      }
    }

    .reject-btn {
      background: $brand_white;
      margin-right: 10px;

      .text {
        color: $brand_tonic;
      }

      &:hover {
        background: $brand_red_light;
      }
    }

    .approve-btn {
      background: $brand_accent;
      margin-left: 10px;

      &:hover {
        background: $brand_primary;
        color: $color_white;
      }
    }
  }
}
</style>
